// Gestão → Central de feedback: sugestões, problemas, elogios e dúvidas (equipe, chatbot, pesquisa, manuais).
// Quem tem feedback.ver vê todos; os demais veem só os próprios. Tratar (responsável/situação/resposta): feedback.tratar.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import suporteApi, { mensagemErro } from "../api";
import { dataBR } from "../comum";
import { usePermissao } from "../permissoes";

const NOMES_AREA = { ALARME: "Suporte Alarme/CFTV/Acesso", COFRES: "Suporte Cofres/Guarda-volumes/Portas", CONTRATACAO: "Contratação",
  SUPERVISAO: "Supervisão de campo", DOCUMENTACAO: "Documentação e Treinamento", GERAL: "Geral" };
const NOMES_TIPO = { SUGESTAO: "Sugestão", PROBLEMA: "Problema", ELOGIO: "Elogio", DUVIDA: "Dúvida" };
const CORES = { NOVO: "#1565c0", EM_ANALISE: "#6a1b9a", EM_ANDAMENTO: "#ef6c00", CONCLUIDO: "#2e7d32", NAO_APLICAVEL: "#616161" };

export const DialogNovoFeedback = ({ open, onClose, onEnviado, vinculo }) => {
  const [f, setF] = useState({ area: "GERAL", tipo: "SUGESTAO", titulo: "", texto: "" });
  useEffect(() => { if (open) setF({ area: "GERAL", tipo: "SUGESTAO", titulo: "", texto: "" }); }, [open]);
  const enviar = async () => {
    try {
      await suporteApi.post("/api/feedback", { ...f, ...(vinculo || {}) });
      toast.success("Feedback enviado. Obrigado!");
      onEnviado && onEnviado();
      onClose();
    } catch (e) {
      toast.error(mensagemErro(e));
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Enviar feedback</DialogTitle>
      <DialogContent>
        <div style={{ display: "flex", gap: 8 }}>
          <TextField id="fb-area" select size="small" variant="outlined" label="Área" fullWidth margin="dense"
            value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })}>
            {Object.entries(NOMES_AREA).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </TextField>
          <TextField id="fb-tipo" select size="small" variant="outlined" label="Tipo" fullWidth margin="dense"
            value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })}>
            {Object.entries(NOMES_TIPO).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </TextField>
        </div>
        <TextField id="fb-titulo" size="small" variant="outlined" label="Assunto" fullWidth margin="dense"
          value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} inputProps={{ maxLength: 150 }} />
        <TextField id="fb-texto" size="small" variant="outlined" label="Descreva" fullWidth margin="dense" multiline minRows={4}
          value={f.texto} onChange={(e) => setF({ ...f, texto: e.target.value })} inputProps={{ maxLength: 4000 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" disabled={f.titulo.trim().length < 3 || f.texto.trim().length < 3} onClick={enviar}>Enviar</Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogTratar = ({ id, onClose, onSalvo, podeTratar, situacoes }) => {
  const [f, setF] = useState(null);
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!id) return;
    suporteApi.get(`/api/feedback/${id}`).then((r) => {
      setD(r.data);
      setF({ status: r.data.status, responsavel: r.data.responsavel || "", resposta: r.data.resposta || "", comentario: "" });
    }).catch((e) => toast.error(mensagemErro(e)));
  }, [id]);
  if (!id || !d || !f) return null;
  const salvar = async () => {
    try {
      await suporteApi.put(`/api/feedback/${id}`, f);
      toast.success("Feedback atualizado");
      onSalvo();
      onClose();
    } catch (e) {
      toast.error(mensagemErro(e));
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>#{d.id} · {d.titulo}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="textSecondary">
          {NOMES_AREA[d.area]} · {NOMES_TIPO[d.tipo]} · origem {d.origem.toLowerCase()} · {d.autor || "—"} · {dataBR(d.criado_em)}
        </Typography>
        <Typography variant="body2" style={{ whiteSpace: "pre-wrap", margin: "10px 0" }}>{d.texto}</Typography>
        {podeTratar ? (
          <>
            <div style={{ display: "flex", gap: 8 }}>
              <TextField id="fb-sit" select size="small" variant="outlined" label="Situação" fullWidth margin="dense"
                value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                {Object.entries(situacoes).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
              <TextField id="fb-resp" size="small" variant="outlined" label="Responsável" fullWidth margin="dense"
                value={f.responsavel} onChange={(e) => setF({ ...f, responsavel: e.target.value })} />
            </div>
            <TextField id="fb-resposta" size="small" variant="outlined" label="Resposta (obrigatória para concluir)" fullWidth margin="dense"
              multiline minRows={3} value={f.resposta} onChange={(e) => setF({ ...f, resposta: e.target.value })} />
            <TextField id="fb-coment" size="small" variant="outlined" label="Comentário interno (histórico)" fullWidth margin="dense"
              value={f.comentario} onChange={(e) => setF({ ...f, comentario: e.target.value })} />
          </>
        ) : d.resposta && <Typography variant="body2"><b>Resposta:</b> {d.resposta}</Typography>}
        <Typography variant="subtitle2" style={{ marginTop: 12 }}>Histórico</Typography>
        {d.historico.map((h) => (
          <Typography key={h.id} variant="caption" component="div">
            {dataBR(h.quando)} · {h.por || "—"}: {h.de ? `${situacoes[h.de] || h.de} → ` : ""}{situacoes[h.para] || h.para}{h.texto ? ` — ${h.texto}` : ""}
          </Typography>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        {podeTratar && <Button color="primary" variant="contained" onClick={salvar}>Salvar</Button>}
      </DialogActions>
    </Dialog>
  );
};

const Feedback = () => {
  const [filtro, setFiltro] = useState({ status: "ABERTOS", area: "", tipo: "", busca: "" });
  const [d, setD] = useState(null);
  const [novo, setNovo] = useState(false);
  const [aberto, setAberto] = useState(null);
  const podeTratar = usePermissao("feedback.tratar");
  const podeVer = usePermissao("feedback.ver");

  const carregar = useCallback(() => {
    suporteApi.get("/api/feedback", { params: filtro }).then((r) => setD(r.data)).catch((e) => toast.error(mensagemErro(e)));
  }, [filtro]);
  useEffect(() => { carregar(); }, [carregar]);

  return (
    <MainContainer>
      <MainHeader>
        <Title>{podeVer ? "Central de feedback" : "Meus feedbacks"}</Title>
        <Button size="small" variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setNovo(true)}>Enviar feedback</Button>
      </MainHeader>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <TextField id="fb-f-sit" select size="small" variant="outlined" label="Situação" style={{ minWidth: 160 }}
          value={filtro.status} onChange={(e) => setFiltro({ ...filtro, status: e.target.value })}>
          <MenuItem value="ABERTOS">Em aberto</MenuItem>
          <MenuItem value="">Todas</MenuItem>
          {d && Object.entries(d.situacoes).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField id="fb-f-area" select size="small" variant="outlined" label="Área" style={{ minWidth: 200 }}
          value={filtro.area} onChange={(e) => setFiltro({ ...filtro, area: e.target.value })}>
          <MenuItem value="">Todas</MenuItem>
          {Object.entries(NOMES_AREA).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField id="fb-f-tipo" select size="small" variant="outlined" label="Tipo" style={{ minWidth: 140 }}
          value={filtro.tipo} onChange={(e) => setFiltro({ ...filtro, tipo: e.target.value })}>
          <MenuItem value="">Todos</MenuItem>
          {Object.entries(NOMES_TIPO).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField id="fb-f-busca" size="small" variant="outlined" label="Buscar" value={filtro.busca}
          onChange={(e) => setFiltro({ ...filtro, busca: e.target.value })} />
      </div>
      <Paper variant="outlined" style={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>#</TableCell><TableCell>Assunto</TableCell><TableCell>Área</TableCell><TableCell>Tipo</TableCell>
              <TableCell>Origem</TableCell><TableCell>Autor</TableCell><TableCell>Situação</TableCell><TableCell>Responsável</TableCell><TableCell>Aberto em</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {d && d.itens.length === 0 && (
              <TableRow><TableCell colSpan={9}><Typography variant="body2" color="textSecondary">Nenhum feedback com estes filtros.</Typography></TableCell></TableRow>
            )}
            {d && d.itens.map((x) => (
              <TableRow key={x.id} hover style={{ cursor: "pointer" }} onClick={() => setAberto(x.id)}>
                <TableCell>{x.id}</TableCell>
                <TableCell>{x.titulo}</TableCell>
                <TableCell>{NOMES_AREA[x.area] || x.area}</TableCell>
                <TableCell>{NOMES_TIPO[x.tipo] || x.tipo}</TableCell>
                <TableCell>{x.origem.toLowerCase()}</TableCell>
                <TableCell>{x.autor || "—"}</TableCell>
                <TableCell><Chip size="small" label={d.situacoes[x.status] || x.status} style={{ background: CORES[x.status], color: "#fff" }} /></TableCell>
                <TableCell>{x.responsavel || "—"}</TableCell>
                <TableCell>{dataBR(x.criado_em)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <DialogNovoFeedback open={novo} onClose={() => setNovo(false)} onEnviado={carregar} />
      <DialogTratar id={aberto} onClose={() => setAberto(null)} onSalvo={carregar} podeTratar={podeTratar}
        situacoes={(d && d.situacoes) || {}} />
    </MainContainer>
  );
};

export default Feedback;
